<template>
    <!-- append-to-body: the tooltip belongs to the viewport, not to the card it
         sits in. Rendered in place, a multiline help text on a field near the
         edge of a panel was clipped by that panel — the reader saw half a
         sentence and no way to reach the rest. -->
    <b-tooltip
        :label="$slots.content ? undefined : label"
        :type="type"
        :position="position"
        :size="size"
        multilined
        append-to-body
    >
        <!-- A list of findings is not a sentence: when the caller has several
             things to say, it hands them over as markup rather than joining
             them into one line nobody parses. -->
        <template v-if="$slots.content" #content>
            <slot name="content" />
        </template>
        <b-icon :icon="icon" :size="iconSize" class="help-tooltip-icon" :class="[iconClass, `is-cursor-${cursor}`]" />
    </b-tooltip>
</template>

<script setup>
defineProps({
    /** The text to show. Optional when the `content` slot carries it instead. */
    label: { type: String, default: '' },
    position: { type: String, default: 'is-top' },
    size: { type: String, default: 'is-small' },
    icon: { type: String, default: 'help-circle-outline' },
    iconSize: { type: String, default: 'is-small' },
    /**
     * The icon's own colour, as a Bulma text helper. Grey is help; a tooltip
     * reporting a problem says so with its icon, since a mark nobody notices
     * is a mark nobody hovers.
     */
    iconClass: { type: String, default: 'has-text-grey' },
    type: { type: String, default: 'is-dark' },
    /**
     * 'help' | 'default'. The `help` cursor is a question mark, and what it
     * promises is an explanation on request. A mark reporting that something
     * is wrong is not a question — it is an answer nobody asked for — so it
     * keeps the ordinary arrow.
     */
    cursor: { type: String, default: 'help' }
});
</script>

<style scoped>
.help-tooltip-icon {
    vertical-align: middle;
}

.help-tooltip-icon.is-cursor-help {
    cursor: help;
}

.help-tooltip-icon.is-cursor-default {
    cursor: default;
}
</style>
